/**
 * 계측 타입별 폼 필드 설정
 *
 * 각 MeasureType에 해당하는 입력 필드 정의
 * 클라이언트 안전 - 순수 TypeScript만 사용
 */
import { MeasureType } from "./types.client";

// 필드 타입 정의
export type FieldType = "number" | "text" | "select";

// 필드 설정
export interface FieldConfig {
  name: string;
  label: string;
  type: FieldType;
  unit?: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
}

// 타입별 폼 설정
export interface MeasureFormConfig {
  fields: FieldConfig[];
}

// 접지저항 측정방법 옵션
const groundResistanceMethodOptions = [
  { value: "3단자법", label: "3단자법" },
  { value: "2단자법", label: "2단자법" },
  { value: "클램프식", label: "클램프식" },
];

// 전자시계 송출방식 옵션
const clockLineMethodOptions = [
  { value: "DC12V", label: "DC 12V" },
  { value: "DC24V", label: "DC 24V" },
  { value: "AC24V", label: "AC 24V" },
  { value: "AC220V", label: "AC 220V" },
];

// 광 송수신 구분 옵션
const opticalDeviceTypeOptions = [
  { value: "광 송신기", label: "광 송신기" },
  { value: "광 수신기", label: "광 수신기" },
];

/**
 * MeasureType별 폼 필드 설정
 */
export const measureFormConfigs: Record<MeasureType, MeasureFormConfig> = {
  // 전기 (7개)
  [MeasureType.AC_VOLTAGE]: {
    fields: [
      { name: "ratedVoltage", label: "정격전압", type: "number", unit: "V", required: true },
      { name: "measuredVoltage", label: "측정전압", type: "number", unit: "V", required: true },
    ],
  },
  [MeasureType.DC_VOLTAGE]: {
    fields: [
      { name: "ratedVoltage", label: "정격전압", type: "number", unit: "V", required: true },
      { name: "measuredVoltage", label: "측정전압", type: "number", unit: "V", required: true },
    ],
  },
  [MeasureType.GROUND_RESISTANCE]: {
    fields: [
      { name: "label", label: "구분", type: "text", placeholder: "예: 제1종 접지" },
      {
        name: "method",
        label: "측정방법",
        type: "select",
        options: groundResistanceMethodOptions,
        required: true,
      },
      { name: "measuredValue", label: "측정값", type: "number", unit: "Ω", required: true },
    ],
  },
  [MeasureType.TERMINATION_RESISTANCE]: {
    fields: [
      { name: "label", label: "구분", type: "text", placeholder: "예: RS-485 회선" },
      { name: "standardValue", label: "기준값", type: "number", unit: "Ω" },
      { name: "measuredValue", label: "측정값", type: "number", unit: "Ω", required: true },
    ],
  },
  [MeasureType.CHARGING_VOLTAGE]: {
    fields: [
      { name: "chargingVoltage", label: "충전전압", type: "number", unit: "V", required: true },
      { name: "chargingCurrent", label: "충전전류", type: "number", unit: "A", required: true },
    ],
  },
  [MeasureType.BATTERY_VOLTAGE]: {
    fields: [
      { name: "chargingVoltage", label: "충전전압", type: "number", unit: "V", required: true },
      { name: "chargingCurrent", label: "충전전류", type: "number", unit: "A", required: true },
    ],
  },
  [MeasureType.CLOCK_LINE_VOLTAGE]: {
    fields: [
      {
        name: "transmissionMethod",
        label: "송출방식",
        type: "select",
        options: clockLineMethodOptions,
        required: true,
      },
      { name: "measuredValue", label: "측정값", type: "number", unit: "V", required: true },
    ],
  },
  // 통신환경 (5개)
  [MeasureType.LINK_SPEED]: {
    fields: [
      {
        name: "designSpeed",
        label: "설계속도",
        type: "text",
        placeholder: "예: 1Gbps",
        required: true,
      },
      {
        name: "measuredSpeed",
        label: "측정속도",
        type: "text",
        placeholder: "예: 1Gbps",
        required: true,
      },
    ],
  },
  [MeasureType.OPTICAL_LEVEL]: {
    fields: [
      { name: "seq", label: "No.", type: "number" },
      {
        name: "deviceType",
        label: "구분",
        type: "select",
        options: opticalDeviceTypeOptions,
        required: true,
      },
      { name: "measuredValue", label: "측정값", type: "number", unit: "dBm", required: true },
    ],
  },
  [MeasureType.TX_OUTPUT]: {
    fields: [
      { name: "standardValue", label: "기준값", type: "number", unit: "mW" },
      { name: "measuredValue", label: "측정값", type: "number", unit: "mW", required: true },
    ],
  },
  [MeasureType.OUTPUT_TERMINAL_LEVEL]: {
    fields: [
      { name: "standardValue", label: "기준값", type: "text", placeholder: "예: 60~80 dBuV" },
      { name: "measuredValue", label: "측정값", type: "number", unit: "dBuV", required: true },
    ],
  },
  [MeasureType.IO_SIGNAL_LEVEL]: {
    fields: [
      { name: "inputStandard", label: "입력 기준값", type: "text", placeholder: "dBuV" },
      { name: "inputMeasured", label: "입력 측정값", type: "number", unit: "dBuV" },
      { name: "outputStandard", label: "출력 기준값", type: "text", placeholder: "dBuV" },
      { name: "outputMeasured", label: "출력 측정값", type: "number", unit: "dBuV" },
    ],
  },
  // 전파환경 (3개)
  [MeasureType.RSRP_TERRESTRIAL]: {
    fields: [
      { name: "location", label: "측정 위치", type: "text" },
      { name: "snr", label: "SNR", type: "text", unit: "dB" },
      { name: "mer", label: "MER", type: "text", unit: "dB" },
      { name: "ber", label: "BER", type: "text" },
      { name: "rssi", label: "RSSI", type: "text", unit: "dBm" },
    ],
  },
  [MeasureType.RSRP_FM_RADIO]: {
    fields: [
      { name: "location", label: "측정 위치", type: "text" },
      { name: "snr", label: "SNR", type: "text", unit: "dB" },
      { name: "mer", label: "MER", type: "text", unit: "dB" },
      { name: "ber", label: "BER", type: "text" },
      { name: "rssi", label: "RSSI", type: "text", unit: "dBm" },
    ],
  },
  [MeasureType.RSRP_SATELLITE]: {
    fields: [
      { name: "location", label: "측정 위치", type: "text" },
      { name: "snr", label: "SNR", type: "text", unit: "dB" },
      { name: "mer", label: "MER", type: "text", unit: "dB" },
      { name: "ber", label: "BER", type: "text" },
      { name: "rssi", label: "RSSI", type: "text", unit: "dBm" },
    ],
  },
  // 무선신호 (2개)
  [MeasureType.EMERGENCY_BELL_SENSITIVITY]: {
    fields: [
      { name: "standardValue", label: "기준값", type: "number", unit: "dBm" },
      { name: "measuredValue", label: "측정값", type: "number", unit: "dBm", required: true },
    ],
  },
  [MeasureType.REMOTE_METERING_SENSITIVITY]: {
    fields: [
      {
        name: "frequencyBand",
        label: "주파수 대역",
        type: "select",
        required: true,
        options: [
          { value: "424-447MHz", label: "424~447 MHz" },
          { value: "920-923MHz", label: "920~923 MHz" },
        ],
      },
      { name: "standardValue", label: "기준값", type: "number", unit: "dBm" },
      { name: "measuredValue", label: "측정값", type: "number", unit: "dBm", required: true },
    ],
  },
  // 전계강도 (2개)
  [MeasureType.COAXIAL_FIELD_STRENGTH]: {
    fields: [
      {
        name: "frequencyBand",
        label: "주파수 대역",
        type: "select",
        required: true,
        options: [
          { value: "54-216MHz", label: "54~216 MHz" },
          { value: "216-400MHz", label: "216~400 MHz" },
          { value: "400MHz+", label: "400 MHz 이상" },
        ],
      },
      { name: "standardValue", label: "기준값", type: "number", unit: "μV/m" },
      { name: "measuredValue", label: "측정값", type: "number", unit: "μV/m", required: true },
    ],
  },
  [MeasureType.WIRELESS_FIELD_STRENGTH]: {
    fields: [
      { name: "standardValue", label: "기준값", type: "number", unit: "μV/m" },
      { name: "measuredValue", label: "측정값", type: "number", unit: "μV/m", required: true },
    ],
  },
  // RF카드 (2개)
  [MeasureType.RF_MAGNETIC_FIELD]: {
    fields: [
      { name: "label", label: "구분", type: "text" },
      { name: "measuredValue", label: "측정값", type: "number", unit: "A/m", required: true },
    ],
  },
  [MeasureType.RF_RECOGNITION_DISTANCE]: {
    fields: [
      {
        name: "frequencyBand",
        label: "주파수/카드타입",
        type: "select",
        required: true,
        options: [
          { value: "125-134kHz", label: "125~134 kHz (ISO 11784/11785)" },
          { value: "13.56MHz-proximity", label: "13.56 MHz 근접식 (ISO 14443)" },
          { value: "13.56MHz-vicinity", label: "13.56 MHz 근거리 (ISO 15693)" },
          { value: "860-960MHz", label: "860~960 MHz (ISO 18000-6C)" },
        ],
      },
      { name: "label", label: "구분", type: "text" },
      {
        name: "recognitionDistance",
        label: "인식거리",
        type: "number",
        unit: "m",
        required: true,
      },
    ],
  },
  // 기타 (3개)
  [MeasureType.TEMPERATURE_HUMIDITY]: {
    fields: [
      { name: "label", label: "구분", type: "text", placeholder: "예: 서버실" },
      { name: "temperature", label: "온도", type: "number", unit: "℃", required: true },
      { name: "humidity", label: "습도", type: "number", unit: "%", required: true },
    ],
  },
  [MeasureType.BATTERY_SPECIFIC_GRAVITY]: {
    fields: [
      { name: "standardValue", label: "기준값", type: "text", placeholder: "예: 1.200~1.215" },
      { name: "measuredValue", label: "측정값", type: "number", unit: "S.G", required: true },
    ],
  },
  [MeasureType.MANHOLE_TOXIC_GAS]: {
    fields: [
      { name: "oxygen", label: "산소(O2)", type: "text", unit: "%" },
      { name: "oxygenResult", label: "산소 결과", type: "text" },
      { name: "hydrogenSulfide", label: "황화수소(H2S)", type: "text", unit: "ppm" },
      { name: "hydrogenSulfideResult", label: "황화수소 결과", type: "text" },
      { name: "carbonMonoxide", label: "일산화탄소(CO)", type: "text", unit: "ppm" },
      { name: "carbonMonoxideResult", label: "일산화탄소 결과", type: "text" },
    ],
  },
};

/**
 * MeasureType에 해당하는 폼 설정 가져오기
 */
export function getMeasureFormConfig(type: MeasureType): MeasureFormConfig {
  return measureFormConfigs[type];
}

/**
 * 폼 데이터 초기값 생성
 */
export function createInitialFormData(type: MeasureType): Record<string, unknown> {
  const config = getMeasureFormConfig(type);
  const initialData: Record<string, unknown> = {};

  for (const field of config.fields) {
    if (field.type === "number") {
      initialData[field.name] = 0;
    } else if (field.type === "select" && field.options?.length) {
      initialData[field.name] = field.options[0].value;
    } else {
      initialData[field.name] = "";
    }
  }

  return initialData;
}
